import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePermissions, PermissionsProvider } from './contexts/PermissionsContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import Suppliers from './components/Suppliers';
import Boxes from './components/Boxes';
import OrganizarCaixa from './components/OrganizarCaixa';
import Coletas from './components/Coletas';
import Invoices from './components/Invoices';
import Costs from './components/Costs';
import Cobrancas from './components/Cobrancas';
import CurrencyConverter from './components/CurrencyConverter';
import PagamentosRetirada from './components/PagamentosRetirada';
import PayCobranca from './components/PayCobranca';
import PickupInfo from './components/PickupInfo';
import Documents from './components/Documents';
import Labels from './components/Labels';
import Tracking from './components/Tracking';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Users from './components/Users';
import Verify from './components/Verify';
import PasswordRecovery from './components/PasswordRecovery';
import TokenExpirationAlert from './components/TokenExpirationAlert';
import Rewards from './components/Rewards';
import SolicitacoesExclusao from './components/SolicitacoesExclusao';
import Catalogo from './components/Catalogo';
import { ROUTE_TO_PAGE_MAP } from './constants/pageRoutes';
import './App.css';

const routeToPageMap = ROUTE_TO_PAGE_MAP;

// Componente para rotas protegidas com verificação de permissões
const ProtectedRoute = ({ children, pageName }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { hasAccessToPage, loading: permissionsLoading, userRole } = usePermissions();
  const location = useLocation();

  // Determinar o nome da página baseado na rota ou prop
  const currentPageName = pageName || routeToPageMap[location.pathname];

  // Mostrar loading enquanto verifica autenticação e permissões
  if (authLoading || permissionsLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f9fafb'
      }}>
        <div className="loading-spinner-large"></div>
        <p style={{ marginTop: '1.5rem', color: '#6b7280', fontSize: '1rem', fontWeight: 500 }}>Carregando sistema...</p>
      </div>
    );
  }

  // Redirecionar para login se não estiver autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Se a rota não está mapeada ou não precisa de verificação de permissão, permitir acesso
  if (!currentPageName) {
    return children;
  }

  // Profile sempre deve ser acessível para usuários autenticados (página do próprio usuário)
  if (currentPageName === 'profile') {
    return children;
  }

  // Verificar se o usuário tem acesso à página
  // Se não tem userRole, permitir acesso apenas ao profile (fallback para casos de configuração)
  if (userRole === null && !permissionsLoading) {
    // Se não tem role, só permitir profile
    if (currentPageName === 'profile') {
      return children;
    }
    // Para outras páginas sem role, redirecionar para a primeira página permitida
    return <RedirectToFirstAllowedPage />;
  }

  // Se tem role, verificar se tem acesso à página
  if (currentPageName && userRole) {
    const hasAccess = hasAccessToPage(currentPageName);
    if (!hasAccess) {
      // Em vez de mostrar "Acesso Negado", redirecionar para a primeira página permitida
      return <RedirectToFirstAllowedPage />;
    }
  }

  return children;
};

// Componente para redirecionar para a primeira página permitida
const RedirectToFirstAllowedPage = () => {
  const { getFirstAllowedPage, loading, userRole, allowedPages } = usePermissions();
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = React.useState(false);

  // Timeout de segurança: após 5 segundos, usar fallback
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTimeoutReached(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Aguardar até que as permissões sejam carregadas completamente
  if (loading && !timeoutReached) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f9fafb'
      }}>
        <div className="loading-spinner-large"></div>
        <p style={{ marginTop: '1.5rem', color: '#6b7280', fontSize: '1rem', fontWeight: 500 }}>Preparando sua área...</p>
      </div>
    );
  }

  // Se ainda não tem role ou páginas permitidas após carregar e timeout
  if ((!userRole || !allowedPages.length) && !loading) {
    // Se já estamos no profile, não redirecionar
    if (location.pathname === '/profile') {
      return null;
    }

    // Se o timeout foi atingido e ainda não temos role, redirecionar para profile como fallback
    if (timeoutReached) {
      return <Navigate to="/profile" replace />;
    }

    // Aguardar um pouco mais para garantir que o role seja encontrado
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f9fafb'
      }}>
        <div className="loading-spinner-large"></div>
        <p style={{ marginTop: '1.5rem', color: '#6b7280', fontSize: '1rem', fontWeight: 500 }}>Preparando sua área...</p>
      </div>
    );
  }

  const firstPage = getFirstAllowedPage();

  // Se a página atual já é a primeira página permitida, não redirecionar
  if (location.pathname === firstPage) {
    return null;
  }

  return <Navigate to={firstPage} replace />;
};

// Componente principal da aplicação
const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <>
      {isAuthenticated && <TokenExpirationAlert />}
      <Router>
        <PermissionsProvider>
        <RealtimeProvider>
        <Routes>
        <Route
          path="/pagar/:chargeId"
          element={<PayCobranca />}
        />
        <Route
          path="/retirada"
          element={<PickupInfo />}
        />
        <Route
          path="/login"
          element={isAuthenticated ? <RedirectToFirstAllowedPage /> : <Login />}
        />
        <Route
          path="/rastreamento"
          element={
            <ProtectedRoute pageName="rastreamento">
              <Layout>
                <Verify />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/password-recovery"
          element={<PasswordRecovery />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute pageName="dashboard">
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute pageName="clients">
              <Layout>
                <Clients />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/solicitacoes-exclusao"
          element={
            <ProtectedRoute pageName="solicitacoes-exclusao">
              <Layout>
                <SolicitacoesExclusao />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/catalogo"
          element={
            <ProtectedRoute pageName="catalogo">
              <Layout>
                <Catalogo />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rewards"
          element={
            <ProtectedRoute pageName="rewards">
              <Layout>
                <Rewards />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute pageName="suppliers">
              <Layout>
                <Suppliers />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boxes"
          element={
            <ProtectedRoute pageName="boxes">
              <Layout>
                <Boxes />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizar-caixa"
          element={
            <ProtectedRoute pageName="organizar-caixa">
              <Layout>
                <OrganizarCaixa />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/coletas"
          element={
            <ProtectedRoute pageName="coletas">
              <Layout>
                <Coletas />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/coleta" element={<Navigate to="/coletas" replace />} />
        <Route
          path="/labels"
          element={
            <ProtectedRoute pageName="labels">
              <Layout>
                <Labels />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tracking"
          element={
            <ProtectedRoute pageName="tracking">
              <Layout>
                <Tracking />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute pageName="invoices">
              <Layout>
                <Invoices />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute pageName="payments">
              <Layout>
                <Cobrancas />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pagamentos-retirada"
          element={
            <ProtectedRoute pageName="pagamentos-retirada">
              <Layout>
                <PagamentosRetirada />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/currency"
          element={
            <ProtectedRoute pageName="currency">
              <Layout>
                <CurrencyConverter />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/costs"
          element={
            <ProtectedRoute pageName="costs">
              <Layout>
                <Costs />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/central-fiscal"
          element={
            <ProtectedRoute pageName="central-fiscal">
              <Layout>
                <Documents />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/documents" element={<Navigate to="/central-fiscal" replace />} />
        <Route
          path="/users"
          element={
            <ProtectedRoute pageName="users">
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute pageName="profile">
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute pageName="settings">
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<RedirectToFirstAllowedPage />} />
        <Route path="*" element={<RedirectToFirstAllowedPage />} />
        </Routes>
        </RealtimeProvider>
        </PermissionsProvider>
      </Router>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
